import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Camera, ArrowRight, ArrowLeft, Sparkles, MapPin, Check } from "lucide-react";
import { useRef } from "react";

const VIBE_OPTIONS = [
  { id: "chill", emoji: "😌", label: "Chill & Lounge" },
  { id: "party", emoji: "🎉", label: "Party & Dance" },
  { id: "rooftop", emoji: "🌃", label: "Rooftop Vibes" },
  { id: "live_music", emoji: "🎵", label: "Live Music" },
  { id: "craft_cocktails", emoji: "🍸", label: "Craft Cocktails" },
  { id: "sports_bar", emoji: "⚽", label: "Sports Bar" },
  { id: "pub_crawl", emoji: "🍺", label: "Pub Crawl" },
  { id: "fine_dining", emoji: "🍽️", label: "Fine Dining" },
  { id: "karaoke", emoji: "🎤", label: "Karaoke" },
  { id: "underground", emoji: "🔊", label: "Underground / EDM" },
  { id: "wine_bar", emoji: "🍷", label: "Wine Bar" },
  { id: "late_night", emoji: "🌙", label: "Late Night" },
];

const CITIES = ["Bangalore", "Mumbai", "Delhi", "Hyderabad", "Pune", "Goa", "Chennai"];

const Onboarding = () => {
  const { user, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState(0);
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [city, setCity] = useState("Bangalore");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [vibes, setVibes] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const totalSteps = 3;

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!user || !e.target.files?.[0]) return;
    setUploading(true);
    try {
      const file = e.target.files[0];
      const ext = file.name.split(".").pop();
      const path = `${user.id}/avatar.${ext}`;
      const { error } = await supabase.storage.from("avatars").upload(path, file, { upsert: true });
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from("avatars").getPublicUrl(path);
      setAvatarUrl(publicUrl);
    } catch (err: any) {
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const toggleVibe = (id: string) => {
    setVibes(prev => prev.includes(id) ? prev.filter(v => v !== id) : prev.length < 5 ? [...prev, id] : prev);
  };

  const handleComplete = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const { error } = await supabase.from("profiles").update({
        display_name: displayName || user.email?.split("@")[0] || "User",
        bio,
        city,
        avatar_url: avatarUrl,
        vibe_preferences: vibes,
        onboarding_completed: true,
      }).eq("user_id", user.id);
      if (error) throw error;
      await refreshProfile();
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      toast({ title: "Error saving profile", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const canNext = step === 0 ? displayName.trim().length >= 2 : step === 1 ? vibes.length >= 1 : true;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-lg"
      >
        {/* Progress */}
        <div className="mb-8 flex items-center gap-2">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-all ${i <= step ? "bg-primary" : "bg-muted"}`}
            />
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* Step 0: Profile Setup */}
          {step === 0 && (
            <motion.div key="profile" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Let's set up your profile</h1>
                <p className="mt-2 text-muted-foreground">Tell your crew who you are</p>
              </div>

              {/* Avatar */}
              <div className="flex justify-center">
                <div className="relative">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="" className="h-24 w-24 rounded-2xl object-cover" />
                  ) : (
                    <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-primary/20 text-4xl font-bold text-primary">
                      {displayName ? displayName.charAt(0).toUpperCase() : "?"}
                    </div>
                  )}
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="absolute -bottom-2 -right-2 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg"
                  >
                    <Camera className="h-4 w-4" />
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" />
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">Display Name *</label>
                <input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="What should your crew call you?"
                  className="w-full rounded-xl border border-border bg-muted px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">Bio</label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Party animal? Craft cocktail nerd? Tell us..."
                  rows={3}
                  className="w-full rounded-xl border border-border bg-muted px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  <MapPin className="mr-1 inline h-4 w-4" /> City
                </label>
                <select
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full rounded-xl border border-border bg-muted px-4 py-3 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  {CITIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
            </motion.div>
          )}

          {/* Step 1: Vibe Quiz */}
          {step === 1 && (
            <motion.div key="vibes" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
              <div>
                <h1 className="text-3xl font-bold text-foreground">What's your vibe? 🎵</h1>
                <p className="mt-2 text-muted-foreground">Pick up to 5 — helps us match venues for your crew</p>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {VIBE_OPTIONS.map(v => {
                  const selected = vibes.includes(v.id);
                  return (
                    <button
                      key={v.id}
                      onClick={() => toggleVibe(v.id)}
                      className={`relative flex flex-col items-center gap-2 rounded-xl border-2 p-4 text-center transition-all ${
                        selected
                          ? "border-primary bg-primary/10 shadow-md"
                          : "border-border bg-card hover:border-primary/30"
                      }`}
                    >
                      {selected && (
                        <div className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary">
                          <Check className="h-3 w-3 text-primary-foreground" />
                        </div>
                      )}
                      <span className="text-2xl">{v.emoji}</span>
                      <span className="text-xs font-medium text-foreground">{v.label}</span>
                    </button>
                  );
                })}
              </div>

              <p className="text-center text-sm text-muted-foreground">
                {vibes.length}/5 selected
              </p>
            </motion.div>
          )}

          {/* Step 2: Ready */}
          {step === 2 && (
            <motion.div key="ready" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-8 text-center">
              <div>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", bounce: 0.5, delay: 0.2 }}
                  className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-3xl bg-primary/20"
                >
                  <Sparkles className="h-12 w-12 text-primary" />
                </motion.div>
                <h1 className="text-3xl font-bold text-foreground">You're all set, {displayName}! 🎉</h1>
                <p className="mt-3 text-muted-foreground">Here's what you can do next:</p>
              </div>

              <div className="space-y-3 text-left">
                {[
                  { emoji: "👥", text: "Create a Krew and invite your squad" },
                  { emoji: "🗳️", text: "Vote on venues together as a group" },
                  { emoji: "💰", text: "Lock deposits so everyone shows up" },
                  { emoji: "🤖", text: "Let AI recommend the perfect spot" },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + i * 0.1 }}
                    className="flex items-center gap-3 rounded-xl border border-border bg-card p-4"
                  >
                    <span className="text-xl">{item.emoji}</span>
                    <span className="text-sm font-medium text-foreground">{item.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation */}
        <div className="mt-8 flex items-center justify-between">
          {step > 0 ? (
            <button
              onClick={() => setStep(s => s - 1)}
              className="flex items-center gap-2 rounded-xl border border-border px-5 py-3 text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          ) : (
            <div />
          )}

          {step < totalSteps - 1 ? (
            <button
              onClick={() => setStep(s => s + 1)}
              disabled={!canNext}
              className="group flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-all glow-neon hover:scale-[1.02] disabled:opacity-50"
            >
              Next <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          ) : (
            <button
              onClick={handleComplete}
              disabled={saving}
              className="group flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-all glow-neon hover:scale-[1.02] disabled:opacity-50"
            >
              {saving ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
              ) : (
                <>
                  Let's Go! <Sparkles className="h-4 w-4" />
                </>
              )}
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default Onboarding;
